import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Badge, Stack, TextField } from '@mui/material'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Stepper from '@mui/material/Stepper'
import { ChevronLeftRounded } from '@mui/icons-material'

import { useStores } from '@trivie/core'
import { Button } from '@trivie/ui-web/components/button'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme'
import { SubscriptionGift } from '@trivie/core/src/models/subscriptions/SubscriptionGift'
import { EmailSelect } from '@trivie/ui-web/components/email-select'

import { RadioCard } from '../create-subscription/radio-card'
import { GiftSubscriptionUsers } from './gift-subscription-users'

interface GiftSubscriptionDialogProps {
  open: boolean
  requestClose: () => void
  productIds: number[]
  productNames?: string[]
  defaultDuration?: number
}

export const GiftSubscriptionDialog = observer((props: GiftSubscriptionDialogProps) => {
  const { open, defaultDuration, requestClose, productIds, productNames } = props
  const { subscriptionStore } = useStores()

  const [activeStep, setActiveStep] = useState(0)
  const [mode, setMode] = useState<'test' | 'direct' | null>(null)
  const [form, setForm] = useState(() =>
    SubscriptionGift.create({ custom_duration_days: defaultDuration || 30 }),
  )
  const [error, setError] = useState('')

  const steps =
    mode === 'direct'
      ? ['Choose Type', 'Invite by Email', 'Review']
      : ['Choose Type', 'Existing Users', 'Invite by Email', 'Review']

  const reviewStepIndex = steps.length - 1
  const successStepIndex = steps.length

  useEffect(() => {
    if (open) {
      setActiveStep(0)
      setMode(null)
      setForm(SubscriptionGift.create({ custom_duration_days: defaultDuration || 30 }))
      setError('')
    }
  }, [open])

  const hasRecipients = form.users.length > 0 || form.validEmails.length > 0

  const stepIsValid = () => {
    if (activeStep === 0) return mode !== null
    if (mode === 'direct') {
      switch (activeStep) {
        case 1:
          return hasRecipients
        case 2:
          return (
            hasRecipients &&
            (form.duration_mode === 'permanent' ||
              (form.custom_duration_days != null && form.custom_duration_days > 0))
          )
        default:
          return false
      }
    }
    // gift mode
    switch (activeStep) {
      case 1:
        return true
      case 2:
        return hasRecipients
      case 3:
        return (
          hasRecipients &&
          (form.duration_mode === 'permanent' ||
            (form.custom_duration_days != null && form.custom_duration_days > 0))
        )
      default:
        return false
    }
  }

  const handleNext = async () => {
    if (activeStep === successStepIndex) {
      if (error) {
        setActiveStep(reviewStepIndex)
        setError('')
      } else {
        handleClose()
      }
      return
    }
    if (activeStep === reviewStepIndex) {
      const { user_ids, emails } = form.payload
      let success: any
      if (mode === 'direct') {
        success = await subscriptionStore.directInvite(
          productIds,
          emails,
          form.duration_mode === 'permanent' ? null : form.custom_duration_days,
        )
      } else {
        success = await subscriptionStore.testSubscription(
          productIds,
          user_ids,
          emails,
          form.duration_mode === 'permanent' ? null : form.custom_duration_days,
        )
      }
      if (!success) {
        setError(
          mode === 'direct'
            ? 'There was an issue inviting users'
            : 'There was an issue sending test subscriptions',
        )
      }
      setActiveStep(successStepIndex)
    } else {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleClose = () => {
    requestClose()
  }

  const getStepContent = (step: number) => {
    if (step === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          <RadioCard
            selected={mode === 'direct'}
            onClick={() => {
              setMode('direct')
              form.set('duration_mode', 'permanent')
            }}
            label="Direct Invite"
            description="Bulk invite existing users by email"
          />
          <RadioCard
            selected={mode === 'test'}
            onClick={() => setMode('test')}
            label="Admin Subscription"
            description="An admin subscription gives your team access to review and test the subscription without
consuming a paid licence."
          />
        </div>
      )
    }

    if (mode === 'direct') {
      switch (step) {
        case 1:
          return (
            <>
              <Text
                sx={{ mb: 1 }}
                text="Paste email addresses below to invite users to this subscription. For the best experience, we recommend adding users in the user section first, as this ensures their profile (First Name, Last Name, etc) is complete at invite."
              />
              <EmailSelect
                style={{ flex: 1 }}
                maxHeight={200}
                selected={[...form.emails]}
                onChange={(val) => form.set('emails', val)}
                helperText={`${form.emails.length}/100 Users`}
                error={form.emails.length >= 100}
                maxEmails={100}
              />
            </>
          )
        case 2:
          return <ReviewStep form={form} productNames={productNames} mode={mode} />
        default:
          return null
      }
    }

    // gift mode
    switch (step) {
      case 1:
        return (
          <GiftSubscriptionUsers
            form={form}
            maxSelections={100}
            totalRecipients={form.users.length}
            maxRecipients={100}
          />
        )
      case 2:
        return (
          <>
            <Text
              sx={{ mb: 1 }}
              text="Paste email addresses below to invite users to this subscription. For the best experience, we recommend adding users in the user section first, as this ensures their profile (First Name, Last Name, etc) is complete at invite."
            />
            <EmailSelect
              style={{ flex: 1 }}
              maxHeight={200}
              selected={[...form.emails]}
              onChange={(val) => form.set('emails', val)}
              helperText={`${form.emails.length}/100 Users`}
              error={form.emails.length >= 100}
              maxEmails={100}
            />
          </>
        )
      case 3:
        return <ReviewStep form={form} productNames={productNames} mode={mode} />
      default:
        return null
    }
  }

  return (
    <div>
      <Dialog
        open={open && activeStep === successStepIndex}
        title={error ? 'Error' : 'Add Subscribers'}
        danger={Boolean(error)}
        onConfirm={handleNext}
        dialogText={
          error ||
          (mode === 'direct'
            ? 'Users invited successfully!'
            : 'Test subscriptions sent successfully!')
        }
        onClose={handleClose}
        maxWidth="xs"
        confirmBtnLabel={error ? 'Back' : 'Close'}
      />
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="md"
        onClose={handleClose}
        sx={{
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        title="Add Subscribers"
        open={open && activeStep !== successStepIndex}
        actions={
          <div
            style={{
              display: 'flex',
              flex: 1,
              justifyContent: activeStep === 0 ? 'flex-end' : 'space-between',
            }}
          >
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                startIcon={<ChevronLeftRounded />}
                text="Back"
                variant="text"
              />
            )}
            <Button
              onClick={handleNext}
              disabled={!stepIsValid()}
              text={activeStep === reviewStepIndex ? 'Send' : 'Next'}
            />
          </div>
        }
      >
        <Stepper
          style={{ justifyContent: 'center', paddingLeft: 0, paddingRight: 0, marginBottom: 32 }}
          nonLinear
          activeStep={activeStep}
          sx={{ '& .MuiStepConnector-root': { flex: 0.5 } }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepButton disableRipple disabled>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(activeStep === 1 && mode !== 'direct' ? { height: 500 } : {}),
          }}
        >
          {getStepContent(activeStep)}
        </div>
      </Dialog>
    </div>
  )
})

const ReviewStep = observer(
  ({
    form,
    productNames,
    mode,
  }: {
    form: any
    productNames?: string[]
    mode: 'test' | 'direct' | null
  }) => {
    const existingUsers: any[] = [...form.users]
    const emails: string[] = [...form.validEmails]

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Duration */}
        {mode !== 'direct' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Text variant="body1Medium" text="Duration" />
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
              <RadioCard
                selected={form.duration_mode === 'permanent'}
                onClick={() => form.set('duration_mode', 'permanent')}
                label="Permanent"
                description="Subscription never expires"
              />
              <RadioCard
                selected={form.duration_mode === 'custom'}
                onClick={() => form.set('duration_mode', 'custom')}
                label="Custom duration"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                  <TextField
                    onClick={(e) => e.stopPropagation()}
                    type="number"
                    size="small"
                    value={form.custom_duration_days ?? ''}
                    onFocus={() => form.set('duration_mode', 'custom')}
                    onBlur={() => form.touch('custom_duration_days')}
                    onChange={(e) => {
                      const val = e.target.value ? parseInt(e.target.value, 10) : null
                      form.set('custom_duration_days', val)
                    }}
                    slotProps={{ htmlInput: { min: 1, max: 365 } }}
                    sx={{ width: 80, backgroundColor: '#fff' }}
                    error={
                      form.touched.get('custom_duration_days') &&
                      Boolean(form.errors.custom_duration_days)
                    }
                  />
                  <Text
                    style={{ color: form.duration_mode === 'custom' ? color.black : color.shade30 }}
                    variant="body2"
                    text="days from today"
                  />
                </div>
              </RadioCard>
            </div>
          </div>
        ) : null}

        {/* Type */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Text variant="body1Medium" text="Type" />
          <Text variant="body1" text={mode === 'direct' ? 'Direct Invite' : 'Test Subscription'} />
        </div>

        {/* Products */}
        {/* {productNames && productNames.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Text variant="body1Medium" text="Subscriptions" />
            {productNames.map((name) => (
              <Text key={name} variant="body2" text={name} />
            ))}
          </div>
        )} */}

        {/* Recipients */}

        <div>
          <Text variant="body1Medium" text="Recipients" />
          <Badge
            badgeContent={existingUsers.length + emails.length}
            sx={{
              '.MuiBadge-badge': {
                left: 0,
                backgroundColor: 'rgb(243,243,243)',
                color: 'text.secondary',
                border: `1px solid rgb(229,229,229)`,
              },
            }}
          />
          <Stack direction="row" flexWrap="wrap" mt={2} gap={1} maxHeight={400} overflow="auto">
            {existingUsers.length > 0 &&
              existingUsers.map((u: any) => (
                <div key={u.id} style={CHIP_CONTAINER}>
                  <Text variant="body1" style={{ color: color.shade70 }} text={u.label} />
                </div>
              ))}
            {emails.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {emails.map((email) => (
                  <div key={email} style={CHIP_CONTAINER}>
                    <Text variant="body1" style={{ color: color.shade70 }} text={email} />
                  </div>
                ))}
              </div>
            )}
            {existingUsers.length === 0 && emails.length === 0 && (
              <Text
                variant="body2"
                style={{ color: color.shade50 }}
                text="No recipients selected"
              />
            )}
          </Stack>
        </div>
      </div>
    )
  },
)

const CHIP_CONTAINER: React.CSSProperties = {
  backgroundColor: '#EFF0F6',
  borderRadius: 6,
  padding: 6,
}
